import { useCallback, useEffect, useMemo } from 'react';
import ReactDom from 'react-dom';
import { useTranslation } from 'react-i18next';

import { TAG_VARIANTS } from '@private/components';

import Button from 'components/Button';
import RightSidebar from 'components/RightSidebar';
import { KEYBOARD_KEYS } from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useBreakpoint } from 'hooks/adaptive/useBreakpoint';
import { IFilterSetting } from 'utils/types';

import { useFilterContext } from '../FilterContext';
import { getAppliedFilters } from '../useFiltration';
import { isAppliedFilterActive } from '../utils/helpers';
import { IAppliedFilterValue } from '../utils/types';

import Body from './components/Body';

import {
  ActionsButtons,
  ApplyButton,
  ButtonTag,
  ShareButton,
  StyledFilterButton,
  StyledTag,
} from './styled';

const rootApp = document.querySelector('#root') || document.body;

const validFilterChecker =
  (ignoreList?: string[]) =>
  ([filterKey, { value }]: [string, IAppliedFilterValue]) =>
    value !== undefined &&
    value !== null &&
    value !== '' &&
    !ignoreList?.includes(filterKey);

const countAppliedFilters = (
  appliedFilters: Record<string, IAppliedFilterValue>,
  ignoreList?: string[],
) => {
  const validEntries = Object.entries(appliedFilters).filter(
    validFilterChecker(ignoreList),
  );

  return {
    active: validEntries.filter(([, filter]) => isAppliedFilterActive(filter)).length,
    total: validEntries.length,
  };
};

const getCountLabel = ({ active, total }: { active: number; total: number }) =>
  active === total ? `${total}` : `${active}/${total}`;

const SidebarFilterButton = () => {
  const { t } = useTranslation();

  const checkWindowWidth = useBreakpoint();

  const {
    canFilter,
    clearAllFilters,
    config: { filtersSections, hidePresets, ignoreList, intlPrefix },
    conflictColumns,
    filters,
    gridColumnsSettings,
    isCreatingShortLink,
    isSidebarOpen,
    onApply,
    onCloseSidebar,
    onPageFiltersChange,
    onShareFilters,
    onToggleSidebar,
    setIsSidebarOpen,
    sidebarFilters,
  } = useFilterContext();

  const hasConflicts = !!conflictColumns.length;

  const filtersSectionsList: [string, Map<string, IFilterSetting>][] = useMemo(
    () => Array.from(filtersSections?.entries() || []),
    [filtersSections],
  );

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      const { key } = e;

      if (key === KEYBOARD_KEYS.ENTER && canFilter && !hasConflicts && isSidebarOpen) {
        e.preventDefault();
        onPageFiltersChange?.({ filters: sidebarFilters, gridColumnsSettings });
      } else if (key === KEYBOARD_KEYS.ESCAPE && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('keyup', onKeyPress);

    return () => {
      document.removeEventListener('keyup', onKeyPress);
    };
  }, [
    onPageFiltersChange,
    canFilter,
    hasConflicts,
    isSidebarOpen,
    sidebarFilters,
    gridColumnsSettings,
    setIsSidebarOpen,
  ]);

  const onApplyFilters = useCallback(() => {
    onApply();
  }, [onApply]);

  const pageFiltersCount = useMemo(() => {
    if (!filters) {
      return { active: 0, total: 0 };
    }
    const appliedFilters = getAppliedFilters({ filters, intlPrefix });
    return countAppliedFilters(appliedFilters, ignoreList);
  }, [filters, ignoreList, intlPrefix]);

  const sidebarFiltersCount = useMemo(
    () => countAppliedFilters(sidebarFilters, ignoreList),
    [sidebarFilters, ignoreList],
  );

  return (
    <>
      <StyledFilterButton
        iconSize={15}
        onClick={onToggleSidebar}
        size={checkWindowWidth(1000) ? 'md' : 'lg'}
        startIcon={<ICONS_MAP.Filters />}
        testIds={{ container: 'filtersButton' }}
        type='button'
        variant='outlined'
      >
        {t('common.filters.title')}
        {!!pageFiltersCount.total && (
          <ButtonTag
            label={getCountLabel(pageFiltersCount)}
            variant={TAG_VARIANTS.PRIMARY}
          />
        )}
      </StyledFilterButton>
      {ReactDom.createPortal(
        <RightSidebar
          buttons={
            <ActionsButtons>
              {!hidePresets && (
                <ShareButton>
                  <Button
                    closeTooltipIn={1500}
                    disabled={
                      (!sidebarFiltersCount.total && !gridColumnsSettings) ||
                      isCreatingShortLink
                    }
                    iconSize={16}
                    onClick={onShareFilters}
                    size='md'
                    startIcon={<ICONS_MAP.Share />}
                    testIds={{ container: 'shareFiltersButton' }}
                    type='button'
                    variant='transparentPrimary'
                  >
                    {t('common.filters.share')}
                  </Button>
                </ShareButton>
              )}
              <ApplyButton
                disabled={!canFilter || hasConflicts}
                onClick={onApplyFilters}
                size='md'
                testIds={{ container: 'applyFiltersButton' }}
                type='button'
                variant='primary'
              >
                {t('common.filters.filter')}
              </ApplyButton>
            </ActionsButtons>
          }
          closeBar={onCloseSidebar}
          customTitleComponent={
            sidebarFiltersCount.total ? (
              <StyledTag
                buttonIconSize={7}
                endIcon={<ICONS_MAP.Close />}
                label={getCountLabel(sidebarFiltersCount)}
                onEndIconClick={clearAllFilters}
                variant={TAG_VARIANTS.PRIMARY}
              />
            ) : undefined
          }
          isOpen={isSidebarOpen}
          saveButtonType='button'
          title={t('common.filters.title')}
        >
          <Body filtersSectionsList={filtersSectionsList} />
        </RightSidebar>,
        rootApp,
      )}
    </>
  );
};

export default appReactMemo(SidebarFilterButton);
